<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ min: 200, max: 900 }),
  },
  min: {
    type: Number,
    default: 200,
  },
  max: {
    type: Number,
    default: 1200,
  },
  step: {
    type: Number,
    default: 10,
  },
  labelFrom: {
    type: String,
    default: '',
  },
  labelTo: {
    type: String,
    default: '',
  },
  unit: {
    type: String,
    default: 'mm',
  },
})

const emit = defineEmits(['update:modelValue'])
const activeThumb = ref(null)
const trackRef = ref(null)

const clamp = (value, minValue, maxValue) => Math.min(Math.max(value, minValue), maxValue)

const normalizedRange = computed(() => {
  const minValue = typeof props.modelValue.min === 'number' ? props.modelValue.min : props.min
  const maxValue = typeof props.modelValue.max === 'number' ? props.modelValue.max : props.max

  return {
    min: clamp(minValue, props.min, props.max),
    max: clamp(maxValue, props.min, props.max),
  }
})

const span = computed(() => props.max - props.min)
const minPercent = computed(() => ((normalizedRange.value.min - props.min) / span.value) * 100)
const maxPercent = computed(() => ((normalizedRange.value.max - props.min) / span.value) * 100)

const updateMin = (value) => {
  const numeric = clamp(Number(value), props.min, normalizedRange.value.max - props.step)
  emit('update:modelValue', {
    min: numeric,
    max: normalizedRange.value.max,
  })
  return numeric
}

const updateMax = (value) => {
  const numeric = clamp(Number(value), normalizedRange.value.min + props.step, props.max)
  emit('update:modelValue', {
    min: normalizedRange.value.min,
    max: numeric,
  })
  return numeric
}

const stopDrag = () => {
  activeThumb.value = null
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', stopDrag)
  window.removeEventListener('pointercancel', stopDrag)
}

const onPointerMove = (event) => {
  if (!activeThumb.value) return
  const rect = trackRef.value?.getBoundingClientRect()
  if (!rect) return
  const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1)
  const value = props.min + ratio * span.value
  if (activeThumb.value === 'min') {
    updateMin(Math.round(value / props.step) * props.step)
  } else {
    updateMax(Math.round(value / props.step) * props.step)
  }
}

const startDrag = (thumb, event) => {
  activeThumb.value = thumb
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', stopDrag)
  window.addEventListener('pointercancel', stopDrag)
  onPointerMove(event)
}

const onThumbKeydown = (thumb, event) => {
  const delta =
    event.key === 'ArrowRight' || event.key === 'ArrowUp'
      ? props.step
      : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
        ? -props.step
        : 0
  if (!delta) return
  event.preventDefault()
  if (thumb === 'min') {
    updateMin(normalizedRange.value.min + delta)
  } else {
    updateMax(normalizedRange.value.max + delta)
  }
}

const handleTrackPointerDown = (event) => {
  const rect = trackRef.value?.getBoundingClientRect()
  if (!rect) return
  const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1)
  const value = props.min + ratio * span.value
  const distanceToMin = Math.abs(value - normalizedRange.value.min)
  const distanceToMax = Math.abs(value - normalizedRange.value.max)
  const thumb = distanceToMin <= distanceToMax ? 'min' : 'max'
  startDrag(thumb, event)
}
</script>

<template>
  <div class="slider-row">
    <div class="slider-row__field">
      <label>{{ props.labelFrom }}</label>
      <div class="slider-row__value-input">
        <input
          type="number"
          :min="props.min"
          :max="props.max"
          :step="props.step"
          :value="normalizedRange.min"
          @change="(e) => (e.currentTarget.value = updateMin(e.currentTarget.value))"
        />
        <span class="slider-row__unit">{{ props.unit }}</span>
      </div>
    </div>
    <div class="slider-row__field">
      <label>{{ props.labelTo }}</label>
      <div class="slider-row__value-input">
        <input
          type="number"
          :min="props.min"
          :max="props.max"
          :step="props.step"
          :value="normalizedRange.max"
          @change="(e) => (e.currentTarget.value = updateMax(e.currentTarget.value))"
        />
        <span class="slider-row__unit">{{ props.unit }}</span>
      </div>
    </div>
  </div>
  <div class="slider-wrapper" ref="trackRef" @pointerdown.prevent="handleTrackPointerDown">
    <div class="slider-track">
      <div
        class="slider-fill"
        :style="{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }"
      ></div>
    </div>
    <button
      type="button"
      role="slider"
      class="slider-thumb slider-thumb--min"
      :style="{ left: `${minPercent}%` }"
      :aria-valuenow="normalizedRange.min"
      :aria-valuemin="props.min"
      :aria-valuemax="normalizedRange.max"
      aria-label="Minimum value"
      @pointerdown.prevent="(event) => startDrag('min', event)"
      @keydown="(event) => onThumbKeydown('min', event)"
    ></button>
    <button
      type="button"
      role="slider"
      class="slider-thumb slider-thumb--max"
      :style="{ left: `${maxPercent}%` }"
      :aria-valuenow="normalizedRange.max"
      :aria-valuemin="normalizedRange.min"
      :aria-valuemax="props.max"
      aria-label="Maximum value"
      @pointerdown.prevent="(event) => startDrag('max', event)"
      @keydown="(event) => onThumbKeydown('max', event)"
    ></button>
  </div>
</template>

<style scoped>
.slider-row {
  display: flex;
  gap: var(--space-16);
  flex-wrap: wrap;
}

.slider-row__field {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  flex: 1;
  min-width: 0;
}

.slider-row label {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--semantic-text-black);
}

.slider-row__value-input {
  padding: var(--space-8);
  border: 1px solid var(--semantic-border-muted);
  /* border-radius: 0.2rem; */
  background: var(--color-base-white);
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: min(7rem, 100%);
}

.slider-row__value-input input {
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--semantic-text-black);
  width: 100%;
}

.slider-row__value-input input:focus-visible {
  outline: none;
}

.slider-row__unit {
  font-size: 0.85rem;
  color: var(--semantic-bg-petrol-strong);
}

.slider-wrapper {
  position: relative;
  height: 2.25rem;
  display: flex;
  align-items: center;
  margin-top: 5px;
  touch-action: none;
}

.slider-track {
  position: absolute;
  inset: 0;
  top: 15px;
  height: 4px;
  border-radius: 999px;
  background: var(--semantic-bg-petrol-muted);
  overflow: hidden;
}

.slider-fill {
  position: absolute;
  height: 100%;
  background: var(--semantic-bg-petrol-strong);
  border-radius: inherit;
}

.slider-thumb {
  position: absolute;
  top: 6px;
  transform: translate(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: var(--semantic-bg-petrol-strong);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
}

.slider-thumb.slider-thumb--min {
  transform: translate(0%);
}

.slider-thumb.slider-thumb--max {
  transform: translate(-100%);
}

.slider-thumb:focus-visible {
  outline: 2px solid var(--semantic-text-muted);
  outline-offset: 4px;
}
</style>
